import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useResolvedPath } from 'react-router-dom';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { constructUrl, getParsedQuery, getValidPaginatorSize } from '@/lib/utils';
import { EventModel, PERFORMANCE_STUDY_STATUS_ID, ProgramModel } from '@/lib/models';
import { eventsService } from '@/lib/services';
import { useAccount, useHandleError, useQuery } from '@/hooks';
import { AsyncPage, FilterTabs, LinkBox, ListResult, Loader, LoadingButton, Select } from '@/components';

enum VIEW {
	ACTIVE = 'ACTIVE',
	PAST = 'PAST',
}

export const PerformanceStudies = () => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const path = useResolvedPath('').pathname;
	const { account } = useAccount();
	const handleError = useHandleError();

	const searchParams = useQuery();
	const hasQuery = useMemo(() => {
		const paramsToIgnore = ['size', 'view'];

		return (
			Object.entries(getParsedQuery(searchParams)).filter(
				([key, value]) => !paramsToIgnore.includes(key) && value !== ''
			).length > 0
		);
	}, [searchParams]);
	const view = searchParams.get('view') ?? VIEW.ACTIVE;
	const pageRef = useRef(0);
	const programId = searchParams.get('programId') ?? '';
	const size = useMemo(() => parseInt(getValidPaginatorSize(searchParams), 10), [searchParams]);

	const [isLoading, setIsLoading] = useState(false);
	const [programOptions, setProgramOptions] = useState<ProgramModel[]>([]);
	const [performanceStudies, setPerformanceStudies] = useState<EventModel[]>([]);
	const [performanceStudiesTotal, setPerformanceStudiesTotal] = useState(0);

	const fetchPrograms = useCallback(async () => {
		if (!account) {
			throw new Error('account is undefined');
		}

		const response = await eventsService.getRelevantPerformanceStudyProgramOptions(view === VIEW.PAST).fetch();
		setProgramOptions(response.programs);
	}, [account, view]);

	const getUpcomingPerformanceStudies = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await eventsService
				.getUpcomingPerformanceStudies({
					size,
					page: String(pageRef.current),
					...(programId && { programId }),
				})
				.fetch();

			setPerformanceStudiesTotal(response.totalCount);
			setPerformanceStudies((previousValue) => {
				if (pageRef.current === 0) {
					return response.events;
				}
				return previousValue.concat(response.events);
			});
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}, [size, programId, handleError]);

	const getPastPerformanceStudies = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await eventsService
				.getPastPerformanceStudies({
					size,
					page: String(pageRef.current),
					...(programId && { programId }),
				})
				.fetch();

			setPerformanceStudiesTotal(response.totalCount);
			setPerformanceStudies((previousValue) => {
				if (pageRef.current === 0) {
					return response.events;
				}
				return previousValue.concat(response.events);
			});
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}, [size, programId, handleError]);

	useEffect(() => {
		pageRef.current = 0;
		if (view === VIEW.ACTIVE) {
			getUpcomingPerformanceStudies();
		} else {
			getPastPerformanceStudies();
		}
	}, [getUpcomingPerformanceStudies, getPastPerformanceStudies, view]);

	const handlePageSizeChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { size: currentTarget.value }, search), { replace: true });
	};

	const handleFilterTabsChange = (value: VIEW) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { view: value }, search));
	};

	const handleProgramSelectChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { programId: currentTarget.value }, search));
	};

	const handleShowMoreButtonClick = () => {
		pageRef.current += 1;
		if (view === VIEW.ACTIVE) {
			getUpcomingPerformanceStudies();
		} else {
			getPastPerformanceStudies();
		}
	};

	return (
		<>
			<Helmet>
				<title>Hackett Connect - Events - Performance Studies</title>
			</Helmet>

			<Container className="pt-7">
				<Row>
					<Col>
						<div className="d-flex align-items-center justify-content-between">
							<h1 className="mb-0 text-primary">{isLoading ? 'Loading...' : 'Performance Studies'}</h1>
						</div>
					</Col>
				</Row>
			</Container>
			<Container className="py-7">
				<Row>
					<Col xs={12} xl={8} className="mb-6">
						<FilterTabs
							className="mb-6"
							value={view}
							tabs={[
								{ title: 'Active & Upcoming', value: VIEW.ACTIVE },
								{ title: 'Results from Past Studies', value: VIEW.PAST },
							]}
							onChange={handleFilterTabsChange}
							aside={
								<div className="d-flex align-items-center">
									<Form.Label className="mb-0">Page Size</Form.Label>
									<Select variant="secondary" value={size} onChange={handlePageSizeChange}>
										<option value="10">10</option>
										<option value="20">20</option>
										<option value="50">50</option>
									</Select>
								</div>
							}
						/>
						{!isLoading && performanceStudies.length === 0 && (
							<p className="mb-6 text-muted text-center">
								<strong>No Results</strong>
							</p>
						)}
						{isLoading && performanceStudies.length <= 0 && (
							<div className="mb-8 d-flex justify-content-center">
								<Loader />
							</div>
						)}
						{performanceStudies.map((event, index) => {
							return (
								<ListResult
									key={`${event.eventId}-${index}`}
									category={event.performanceStudyStatusId ?? ''}
									date={`${event.durationDescription ?? ''}`}
									to={`${path}/${event.eventId}`}
									title={event.name}
									description={event.listDescription}
									imageSrc={event.imageUrl}
								>
									{event.performanceStudyStatusId !== PERFORMANCE_STUDY_STATUS_ID.CLOSED &&
										event.registrationUrl && (
											<div>
												<Button
													className="mt-2"
													variant={
														event.performanceStudyStatusId ===
														PERFORMANCE_STUDY_STATUS_ID.OPEN
															? 'primary'
															: 'outline-primary'
													}
													size="sm"
													href={event.registrationUrl}
													target="_blank"
													rel="noopener"
												>
													{event.performanceStudyStatusId ===
														PERFORMANCE_STUDY_STATUS_ID.OPEN && 'Take Now'}
													{event.performanceStudyStatusId ===
														PERFORMANCE_STUDY_STATUS_ID.UPCOMING && 'Register'}
												</Button>
											</div>
										)}
								</ListResult>
							);
						})}
						<hr className="mb-8" />
						<div className="d-flex align-items-center justify-content-end">
							<span className="text-muted mr-4">
								Showing 1-{performanceStudies.length}
								{hasQuery && <>&nbsp;of {performanceStudiesTotal}</>}
							</span>
							{performanceStudies.length < performanceStudiesTotal && (
								<LoadingButton
									isLoading={isLoading}
									variant="outline-info"
									onClick={handleShowMoreButtonClick}
								>
									Show {size} More
								</LoadingButton>
							)}
						</div>
					</Col>
					<Col xs={12} xl={4}>
						<Card className="mb-2">
							<Card.Body>
								<div className="mb-4 d-flex align-items-center justify-content-between">
									<Card.Title className="mb-0">Filters</Card.Title>
								</div>
								<AsyncPage fetchData={fetchPrograms}>
									<Form.Group>
										<Form.Label>Program</Form.Label>
										<Select value={programId} onChange={handleProgramSelectChange}>
											<option value="">All Programs</option>;
											{programOptions.map((po) => {
												return (
													<option key={po.programId} value={po.programId}>
														{po.name}
													</option>
												);
											})}
										</Select>
									</Form.Group>
								</AsyncPage>
							</Card.Body>
						</Card>
						<LinkBox
							sections={[
								{
									title: "Can't find what you're looking for?",
									links: [{ title: 'Contact Hackett', to: '/contact', showIcon: true }],
								},
							]}
						/>
					</Col>
				</Row>
			</Container>
		</>
	);
};
